import cloneDeep from 'clone-deep'
import { InputType, IS_MAIN_COMPANY } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { deleteVendorOptions, getVendorOptions, updateVendorOptions, updateVendorProps } from 'services'
import { svgLoading } from 'stories/assets'
import { Button, ButtonGroup } from 'stories/components'
import { confirm, InputConvert, InputValidate, openAuditLog } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from './constants'
import { IVendorData } from './types'

interface IVendorResponse {
  enabled: boolean
  useMainKey: boolean
  value: IVendorData
  options: Record<string, InputType>
  mainKeyOptions: Record<string, InputType>
  mainKeyValue: Record<string, any>
  description?: string
}

export const CredentialConfig = ({
  title,
  type,
  sectionTitle,
}: {
  title: string
  type: string
  /** Optional heading above the credential form (e.g. "Push" on Vendors → Nexys). */
  sectionTitle?: string
}) => {
  const [isLoading, setIsLoading] = useState<string>('')
  const [selectedMenu, setSelectedMenu] = useState<string>('')
  const [inputOptions, setInputOptions] = useState<Record<string, InputType>>({})
  const [inputMainKeyOptions, setInputMainKeyOptions] = useState<Record<string, InputType>>({})

  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [mainKeyInputs, setMainKeyInputs] = useState<Record<string, InputType>>({})

  const [mainKeyData, setMainKeyData] = useState<Record<string, any>>()

  const [enabled, setEnabled] = useState(false)
  const [useMainKey, setUseMainKey] = useState<boolean>()
  const [values, setValues] = useState<IVendorData>({})
  const [description, setDescription] = useState<string>('')

  useEffect(() => {
    setIsLoading('loading')
    getVendorOptions(type)
      .then((data: IVendorResponse) => {
        const { enabled, useMainKey, value, options, mainKeyOptions, mainKeyValue, description } = data
        setEnabled(enabled)
        setUseMainKey(IS_MAIN_COMPANY ? undefined : useMainKey)
        // setUseMainKey(useMainKey)
        setInputOptions(options)
        setInputMainKeyOptions(mainKeyOptions)
        setValues(value)
        setSelectedMenu(Object.keys(value)[0])
        setMainKeyData(mainKeyValue)
        setDescription(description || '')
      })
      .finally(() => setIsLoading(''))
  }, [])

  useEffect(() => {
    const newInputs: Record<string, InputType> = cloneDeep({
      title: defaultInputs.title,
      ...inputOptions,
    })

    if (!selectedMenu) return
    const selectedValue = values[selectedMenu]
    Object.keys(selectedValue).forEach((v) => {
      if (newInputs[v]) newInputs[v].value = selectedValue[v]
    })
    setInputs(newInputs)
  }, [inputOptions, values, selectedMenu])

  useEffect(() => {
    if (!mainKeyData) return
    if (!Object.keys(inputMainKeyOptions).length) return

    const newInputs: Record<string, InputType> = cloneDeep({
      ...inputMainKeyOptions,
    })

    Object.keys(newInputs).forEach((v) => (newInputs[v].value = mainKeyData[v]))
    setMainKeyInputs(newInputs)
  }, [inputMainKeyOptions])

  const menus = useMemo(() => {
    const menus: Record<string, string> = {}
    Object.keys(values).forEach((key) => {
      const item = values[key]
      menus[key] = `${item.title}${item.isActive ? ' (Active)' : ''}`
    })
    return menus
  }, [values])

  const active = useMemo(() => values[selectedMenu]?.isActive, [values, selectedMenu])
  const hasIndividualActive = useMemo(() => !!inputOptions.isActive, [inputOptions])

  const onActive = () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    Object.keys(newStats).forEach((key) => {
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
    })
    if (hasError) {
      setInputs(newStats)
      return
    }

    const newValues = cloneDeep(values)
    Object.keys(newValues).forEach((key) => {
      if (key === selectedMenu) newValues[key].isActive = true
      else newValues[key].isActive = false
    })

    setIsLoading('active')
    updateVendorOptions(type, newValues)
      .then(({ success }) => {
        if (!success) return
        setValues(newValues)
      })
      .finally(() => setIsLoading(''))
  }

  const onAddNew = () => {
    const newInputs = cloneDeep(inputs)
    const newKey = `${Date.now()}`
    Object.keys(newInputs).forEach((key) => (newInputs[key].value = ''))
    newInputs.title.value = 'New'
    setInputs(newInputs)
    const newValue = cloneDeep(values)
    newValue[newKey] = { title: 'New', isActive: false }
    setValues(newValue)
    setSelectedMenu(newKey)

    setIsLoading('add')
    updateVendorOptions(type, newValue)
      .then(({ success }) => {
        if (!success) return
        success && toast(`New credential has been added.`, { type: 'info' })
      })
      .finally(() => setIsLoading(''))
  }

  const onChange = async (key: string, value: string | boolean) => {
    let newState = cloneDeep(inputs)
    newState[key].value = InputConvert(newState[key], value)
    newState[key].error = ''
    setInputs(newState)
  }

  const onChangeMainKeyInputs = async (key: string, value: string | boolean) => {
    let newState = cloneDeep(mainKeyInputs)
    newState[key].value = InputConvert(newState[key], value)
    newState[key].error = ''
    setMainKeyInputs(newState)
  }

  const onDelete = async () => {
    const result = await confirm('Are you sure you want to delete!')
    if (!result) return

    setIsLoading('delete')
    deleteVendorOptions(type, selectedMenu)
      .then(({ success, updatedValues }) => {
        if (!success) return
        toast(`Seletected credential has been deleted.`, { type: 'info' })
        setSelectedMenu(Object.keys(updatedValues)[0])
        setValues(updatedValues)
      })
      .finally(() => setIsLoading(''))
  }

  const showUseMainKeyHistory = () => {
    openAuditLog({
      table: 'Vendor',
      field: 'useMainKey',
      fieldLabel: defaultInputs.useMainKey.title,
      keys: { type, field: 'useMainKey' },
      map: { true: 'Yes', false: 'No' },
    })
  }

  const onUpdateProps = (key: string, value: boolean) => {
    setIsLoading('loading')
    updateVendorProps(type, { [key]: value })
      .then(({ success }) => {
        if (!success) return
        toast(`${title} has been updated`, { type: 'info' })
        if (key == 'enabled') setEnabled(value)
        if (key == 'useMainKey') setUseMainKey(value)
      })
      .finally(() => setIsLoading(''))
  }

  const onSubmit = () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (!disabled && value !== undefined) data[key] = value
    }
    if (hasError) {
      setInputs(newStats)
      return
    }

    const newValue = cloneDeep(values)
    Object.keys(data).forEach((key) => {
      newValue[selectedMenu][key] = data[key]
    })

    setValues(newValue)

    setIsLoading('submit')
    updateVendorOptions(type, newValue)
      .then(({ success }) => {
        success && toast(`Credential(${data.title}) has been updated.`, { type: 'info' })
      })
      .finally(() => setIsLoading(''))
  }

  const onSubmitMainKey = () => {
    if (!mainKeyData) return

    let hasError = false
    const newStats = cloneDeep(mainKeyInputs)
    const newData: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (!disabled && value !== undefined) newData[key] = value
    }
    if (hasError) {
      setMainKeyInputs(newStats)
      return
    }

    setIsLoading('submit')

    updateVendorProps(type, { mainKeyValue: newData })
      .then(({ success }) => {
        if (!success) return
        toast(`${title} has been updated`, { type: 'info' })
        setMainKeyData(newData)
      })
      .finally(() => setIsLoading(''))
  }

  const renderOriginalInputs = () => {
    return (
      <>
        <RenderInput
          input={
            {
              ...defaultInputs.enabled,
              value: enabled,
              disabled: !!isLoading,
            } as any
          }
          Key="enabled"
          onChange={(_: string, v: boolean) => onUpdateProps('enabled', v)}
        />

        <div className="py-3">
          <ButtonGroup title={menus} value={selectedMenu} onChange={(value) => setSelectedMenu(value)} />
          <Button loading={isLoading === 'add'} onClick={() => onAddNew()} disabled={!!isLoading}>
            + Add
          </Button>
        </div>

        {Object.keys(inputs).map((key: any, index) => {
          if (!enabled) inputs[key].required = false
          let input = inputs[key]
          return (
            <div className={`input md:max-w-2xl mb-3`} key={index}>
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}

        <div className="mt-5">
          <Button onClick={onSubmit} loading={isLoading === 'submit'} disabled={!!isLoading}>
            Submit
          </Button>
          <Button
            onClick={onDelete}
            loading={isLoading === 'remove'}
            color="red"
            disabled={active ? active : !!isLoading}
          >
            Delete
          </Button>
          {!hasIndividualActive && (
            <Button onClick={onActive} loading={isLoading === 'active'} disabled={active ? active : !!isLoading}>
              Make active
            </Button>
          )}
        </div>
      </>
    )
  }

  const renderMainKeyInputs = () => {
    if (!Object.keys(inputMainKeyOptions).length) return null

    return (
      <>
        {Object.keys(mainKeyInputs).map((key: any, index) => {
          let input = mainKeyInputs[key]
          return (
            <div className={`input md:max-w-2xl mb-3`} key={index}>
              <RenderInput input={input} Key={key} onChange={onChangeMainKeyInputs} />
            </div>
          )
        })}
        <div className="mt-5">
          <Button onClick={onSubmitMainKey} loading={isLoading === 'submit'} disabled={!!isLoading}>
            Submit
          </Button>
        </div>
      </>
    )
  }

  return (
    <div className="CreditReports-container">
      <h2 className="text-xl font-bold flex items-center">
        {title}
        {!!isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      {description && <p className="mt-2 text-sm text-gray-500 max-w-2xl">{description}</p>}
      {sectionTitle && (
        <div className="mt-4 mb-1">
          <RenderInput input={{ inputType: 'section', title: sectionTitle }} Key="vendorSectionTitle" onChange={() => {}} />
        </div>
      )}
      <div className="mt-5 w-full">
        {useMainKey !== undefined && (
          <div className="mb-4">
            <RenderInput
              input={
                {
                  ...defaultInputs.useMainKey,
                  value: useMainKey,
                  disabled: !!isLoading,
                  history: true,
                } as any
              }
              Key="useMainKey"
              onChange={(_: string, v: boolean) => onUpdateProps('useMainKey', v)}
              showHistory={showUseMainKeyHistory}
            />
          </div>
        )}

        {!useMainKey ? renderOriginalInputs() : renderMainKeyInputs()}
      </div>
    </div>
  )
}
